import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import getLang from 'I18n/utils/getLang';
import { KEY_PREFIX } from 'marketplace-ui-common/hooks/useSearchParams';
import { getIsPrerenderUserAgent } from 'marketplace-ui-common/utils/userAgent';
import { useUnifiedDetails } from 'marketplace-ui-common/hooks/useUnifiedDetails';
import { Redirect, generatePath, useLocation } from 'quartz-routing/react-router';
import { useMemo } from 'react';
import { StorefrontPageMetadata } from '../../components/StorefrontPageMetadata';
import { Paths } from '../../constants/paths';
import { Subscreens } from '../../constants/usage';
import { useAppContext } from '../../context/AppContext';
import { useFilterConfigData } from '../../filters/hooks/useFilterConfig';
import { useTrackProxyRedirect } from '../../hooks/useTrackProxyRedirect';
import { getFullPublicUrl } from '../../utils/urls';
import { useParsedSearchResultsPathParams } from '../hooks/useParsedSearchResultsPathParams';
import { jsx as _jsx } from "react/jsx-runtime";
function PrerenderLegacyListingRedirect({
  listingSlug
}) {
  const listingPath = generatePath(Paths.LISTING_DETAILS, {
    listingSlug
  });
  const appContext = useAppContext();
  const {
    unifiedDetailsLoading,
    unifiedDetailsError
  } = useUnifiedDetails({
    appContext,
    slug: listingSlug,
    language: getLang()
  });
  if (unifiedDetailsLoading) {
    return null;
  }
  if (!unifiedDetailsError) {
    return /*#__PURE__*/_jsx(StorefrontPageMetadata, {
      prerenderStatusCode: 301,
      prerenderHeaders: {
        Location: getFullPublicUrl(listingPath)
      }
    });
  }

  // Listing not found (or error): redirect to the listing page so
  // ListingDetails can render the appropriate 404/error state for Prerender.
  return /*#__PURE__*/_jsx(Redirect, {
    to: listingPath
  });
}
function applySlugFilter(filter, slug, urlParams) {
  var _filter$options$find;
  if (!filter || !slug) {
    return {};
  }
  const optionValue = (_filter$options$find = filter.options.find(option => option.slug === slug)) === null || _filter$options$find === void 0 ? void 0 : _filter$options$find.value;
  if (optionValue === undefined || optionValue === null) {
    return {};
  }
  const field = String(filter.field);
  const value = String(optionValue);
  urlParams.set(`${KEY_PREFIX}${field}`, value);
  return {
    field,
    value
  };
}
const PROXY_SUBSCREENS = {
  APP: {
    all: Subscreens.PROXY_ALL_APPS,
    filtered: Subscreens.PROXY_APP_CATEGORY
  },
  AGENT: {
    all: Subscreens.PROXY_ALL_AGENTS,
    filtered: Subscreens.PROXY_AGENT_TEAM
  },
  WORKFLOW_TEMPLATE: {
    all: Subscreens.PROXY_ALL_WORKFLOW_TEMPLATES,
    filtered: Subscreens.PROXY_WORKFLOW_TEMPLATE_OBJECTIVE
  }
};
export function ProductsSearchResultsProxy({
  productType
}) {
  var _data$result, _data$result2, _data$result3;
  const parsedPathParams = useParsedSearchResultsPathParams();
  const location = useLocation();
  const {
    data,
    loading
  } = useFilterConfigData();
  const CATEGORY = data === null || data === void 0 || (_data$result = data.result) === null || _data$result === void 0 || (_data$result = _data$result.filterConfigs) === null || _data$result === void 0 ? void 0 : _data$result.CATEGORY;
  const AGENT_TEAM = data === null || data === void 0 || (_data$result2 = data.result) === null || _data$result2 === void 0 || (_data$result2 = _data$result2.filterConfigs) === null || _data$result2 === void 0 ? void 0 : _data$result2.AGENT_TEAM;
  const OBJECTIVE = data === null || data === void 0 || (_data$result3 = data.result) === null || _data$result3 === void 0 || (_data$result3 = _data$result3.filterConfigs) === null || _data$result3 === void 0 ? void 0 : _data$result3.OBJECTIVE;
  const {
    pathname,
    params,
    isLegacyListingRedirect,
    subscreen,
    filters,
    filterType,
    filterValue
  } = useMemo(() => {
    if (loading) {
      return {
        pathname: Paths.SEARCH_RESULTS,
        params: new URLSearchParams(),
        isLegacyListingRedirect: false,
        subscreen: '',
        filters: [],
        filterType: undefined,
        filterValue: undefined
      };
    }
    const urlParams = new URLSearchParams(location.search);
    let resolvedField;
    let resolvedValue;
    let hasSlug = false;
    if (productType === 'APP' && parsedPathParams.categorySlug) {
      hasSlug = true;
      const resolved = applySlugFilter(CATEGORY, parsedPathParams.categorySlug, urlParams);
      resolvedField = resolved.field;
      resolvedValue = resolved.value;

      // The legacy app marketplace used /apps/:slug for both categories and
      // individual app listings. If the slug doesn't match a category, assume
      // it's a listing slug and redirect to the listing details page.
      if (!resolved.field) {
        return {
          pathname: generatePath(Paths.LISTING_DETAILS, {
            listingSlug: parsedPathParams.categorySlug
          }),
          params: new URLSearchParams(),
          isLegacyListingRedirect: true,
          subscreen: '',
          filters: [],
          filterType: undefined,
          filterValue: undefined
        };
      }
    } else if (productType === 'AGENT') {
      hasSlug = Boolean(parsedPathParams.teamSlug);
      const resolved = applySlugFilter(AGENT_TEAM, parsedPathParams.teamSlug, urlParams);
      resolvedField = resolved.field;
      resolvedValue = resolved.value;
    } else if (productType === 'WORKFLOW_TEMPLATE') {
      hasSlug = Boolean(parsedPathParams.objectiveSlug);
      const resolved = applySlugFilter(OBJECTIVE, parsedPathParams.objectiveSlug, urlParams);
      resolvedField = resolved.field;
      resolvedValue = resolved.value;
    }
    urlParams.set(`${KEY_PREFIX}PRODUCT_TYPE`, productType);
    if (parsedPathParams.page) {
      urlParams.set(`${KEY_PREFIX}page`, parsedPathParams.page.toString());
    }
    return {
      pathname: Paths.SEARCH_RESULTS,
      params: urlParams,
      isLegacyListingRedirect: false,
      subscreen: hasSlug ? PROXY_SUBSCREENS[productType].filtered : PROXY_SUBSCREENS[productType].all,
      filters: resolvedField ? [resolvedField] : [],
      filterType: resolvedField,
      filterValue: resolvedValue ? [resolvedValue] : undefined
    };
  }, [AGENT_TEAM, CATEGORY, OBJECTIVE, loading, location.search, parsedPathParams, productType]);
  useTrackProxyRedirect({
    filterType,
    filterValue,
    filters,
    loading,
    pageNumber: parsedPathParams.page,
    skip: isLegacyListingRedirect,
    subscreen,
    type: productType
  });
  if (loading) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      grow: true,
      size: "medium",
      marginBlockStart: "1600"
    });
  }
  if (isLegacyListingRedirect && getIsPrerenderUserAgent()) {
    return /*#__PURE__*/_jsx(PrerenderLegacyListingRedirect, {
      listingSlug: parsedPathParams.categorySlug
    });
  }
  const search = params.toString();
  return /*#__PURE__*/_jsx(Redirect, {
    to: {
      pathname,
      search: search ? `?${search}` : ''
    }
  });
}